/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Loader2, Send, X, MessageSquare, RotateCcw } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { ChatCapabilityLinkDto } from '../../api';
import { cn } from '../../lib/utils';
import { SignUpButton } from '@clerk/clerk-react';

const STARTER_PROMPTS = [
/*   'How can I make the most of my practice routine?',
  'How do I change strings on Floyd Rose?',
  'Tell me about the Ultra Luxe Strat', */
  'Why should I learn the major scale?',
  //'How do I learn all the notes on the fretboard?',
  //'How do I connect pentatonic box positions?',
  //'Give me some cool Alice In Chains riffs.',
  'How do I connect pentatonic box positions?',
  'Show me the A Minor Pentatonic scale',
  'Show me the D Minor 7 Chord',
];

const TEACH_MODE_STARTER_PROMPTS = [
  'What are modes and why should I care about them?',
  'Why do some chord progressions sound sad?',
  'Teach me how to play a blues progression in A',
];

const THINKING_VERBS = [
  'riffing',
  'grooving',
  'shredding',
  'strumming',
  'picking',
  'jamming',
  'noodling',
  'improvising',
  'tuning up',
  'fingering',
  'bending',
  'sliding',
  'hammering on',
  'overdriving',
  'looking for picks in the dryer', // oddball
  'percolating', // oddball
  'contemplating', // oddball
  'pondering', // oddball
  'cogitating', // oddball
  'scheming', // oddball
];

interface ChatPanelProps {
  onClose?: () => void;
  onCapabilityLink?: (link: ChatCapabilityLinkDto) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ onClose, onCapabilityLink }) => {
  const {
    messages,
    sendMessage,
    isSending,
    isInitializing,
    pendingClarification,
    resolveClarification,
    suggestions,
    resetChat,
    capabilityLinks,
    hasReachedLimit,
    conversationCount,
    conversationLimit,
    isAnonymous,
    anonymousChatUsed,
    teachingMode,
    teachingModeAvailable,
    setTeachingMode,
  } = useChat();

  const [inputValue, setInputValue] = useState('');
  const [thinkingVerb, setThinkingVerb] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const scrollOnNextUpdate = useRef(false);

  useEffect(() => {
    if (isSending) {
      const randomVerb = THINKING_VERBS[Math.floor(Math.random() * THINKING_VERBS.length)];
      setThinkingVerb(randomVerb);
      // Scroll to show the thinking indicator for follow-up questions
      if (messages.length > 0) {
        scrollOnNextUpdate.current = true;
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    } else {
      scrollOnNextUpdate.current = false;
    }
  }, [isSending]);

  // Scroll to show the beginning of the new response on follow-up questions
  useEffect(() => {
    if (scrollOnNextUpdate.current) {
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages.length]);

  const handleSend = async () => {
    if (!inputValue.trim()) {
      return;
    }
    const content = inputValue;
    setInputValue('');
    await sendMessage(content);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleResetChat = async () => {
    await resetChat({ clearContexts: true });
  };

  const handleCapabilityClick = (link: ChatCapabilityLinkDto) => {
    if (onCapabilityLink) {
      onCapabilityLink(link);
    }
  };

  const showStarterPrompts = messages.length === 0 && !isInitializing;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col border-b flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div>
              <p className="font-semibold">Harmonic Companion</p>
              <p className="text-xs text-muted-foreground">
                {teachingMode
                  ? 'Multiple experts guide your learning'
                  : 'Ask a question about guitar'}
              </p>
            </div>
          </div>
          {teachingModeAvailable && (
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-muted-foreground cursor-pointer">Teach</label>
              <Switch
                checked={teachingMode}
                onCheckedChange={setTeachingMode}
                disabled={isSending || isInitializing}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResetChat}
              disabled={isSending || isInitializing || hasReachedLimit}
              title="Start a new chat (clears context)"
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              <span>New chat</span>
            </Button>
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {conversationLimit !== 'unlimited' && (
          <div className="px-4 pb-2">
            <p className="text-xs text-muted-foreground">
              {isAnonymous ? (
                anonymousChatUsed ? (
                  <>
                    You've used your free question.{' '}
                    <SignUpButton mode="modal">
                      <button className="text-primary hover:underline">Sign up free</button>
                    </SignUpButton> for 5 chats/month
                  </>
                ) : (
                  <>
                    1 free question available ·{' '}
                    <SignUpButton mode="modal">
                      <button className="text-primary hover:underline">Sign up</button>
                    </SignUpButton> for 5/month
                  </>
                )
              ) : (
                <>
                  {conversationCount} of {conversationLimit} conversations this month
                  {hasReachedLimit && (
                    <span className="ml-1 text-orange-600">
                      · <a href="/pricing" className="text-primary hover:underline">Upgrade for unlimited</a>
                    </span>
                  )}
                </>
              )}
            </p>
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0 px-4">
        <div className="flex flex-col gap-4 py-4">
          {hasReachedLimit && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm">
              {isAnonymous ? (
                <>
                  <p className="font-semibold text-orange-900 mb-2">
                    Want more? Sign up free!
                  </p>
                  <p className="text-orange-800 mb-3">
                    You've used your 1 free question. Sign up for a free account to get 5 conversations per month, or go Pro for unlimited.
                  </p>
                  <div className="flex gap-2">
                    <SignUpButton mode="modal">
                      <button className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                        Sign Up Free
                      </button>
                    </SignUpButton>
                    <a
                      href="/pricing"
                      className="inline-block rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10"
                    >
                      View Pro
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-semibold text-orange-900 mb-2">
                    Monthly conversation limit reached
                  </p>
                  <p className="text-orange-800 mb-3">
                    You've used all {conversationLimit} conversations for this month. Upgrade to Pro for unlimited conversations with the Harmonic Companion.
                  </p>
                  <a
                    href="/pricing"
                    className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    Upgrade to Pro
                  </a>
                </>
              )}
            </div>
          )}
          {showStarterPrompts && !hasReachedLimit && (
            <div className="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-4 text-sm text-muted-foreground">
              <p className="mb-3 font-medium text-foreground">Example questions:</p>
              <div className="flex flex-wrap gap-2">
                {(teachingMode ? TEACH_MODE_STARTER_PROMPTS : STARTER_PROMPTS).map((prompt) => (
                  <Button
                    key={prompt}
                    size="sm"
                    variant="secondary"
                    className="whitespace-normal text-left"
                    onClick={() => sendMessage(prompt)}
                    disabled={isSending}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {messages.map((message, index) => (
            <div
              key={message.id}
              ref={index === messages.length - 1 ? lastMessageRef : null}
              className={cn('flex flex-col gap-2', {
                'items-end text-right': message.role === 'user',
              })}
            >
              <div
                className={cn('rounded-lg px-3 py-2 text-sm shadow-sm', {
                  'bg-primary text-primary-foreground': message.role === 'user',
                  'bg-muted text-foreground': message.role !== 'user',
                })}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children }) => {
                      const text = String(children);
                      const isCitation = /^S\d+$/.test(text);
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className={isCitation ? 'text-primary font-semibold no-underline' : 'text-primary underline'}
                        >
                          {isCitation ? `[${text}]` : children}
                        </a>
                      );
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
              {message.citations && message.citations.length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {message.citations.map((citation) => (
                    <a
                      key={citation.id}
                      href={citation.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline"
                    >
                      [{citation.id}] {citation.title}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}

          {isSending && (
            <div className="flex flex-col gap-2">
              <div className="rounded-lg bg-muted px-3 py-2 text-sm shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="italic text-xs text-muted-foreground">
                    {thinkingVerb}...
                  </span>
                  <span className="flex gap-0.5">
                    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>•</span>
                    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>•</span>
                    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>•</span>
                  </span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />

          {pendingClarification && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <p className="font-medium text-amber-900">
                {pendingClarification.prompt}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {pendingClarification.options.map((option) => (
                  <Button
                    key={option.id}
                    size="sm"
                    variant="secondary"
                    onClick={() => resolveClarification(option.id)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Capability links section - hidden for now until capability mapper is refined
      {capabilityLinks.filter(link => link.capabilityCode !== 'LICK_PLAY_AUDIO').length > 0 && (
        <div className="hidden md:block border-t px-4 py-3 flex-shrink-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Try these Guituitive tools
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {capabilityLinks
              .filter(link => link.capabilityCode !== 'LICK_PLAY_AUDIO') // Hide demonstration lick
              .map((link) => (
              <Button
                key={`${link.capabilityCode}:${link.actionCode}`}
                variant="outline"
                size="sm"
                className="h-auto justify-start whitespace-normal text-left"
                onClick={() => handleCapabilityClick(link)}
              >
                <div>
                  <p className="text-sm font-medium leading-tight">{link.capabilityName}</p>
                  <p className="text-xs text-muted-foreground">
                    {link.description || `Invoke ${link.actionCode}`}
                  </p>
                </div>
              </Button>
            ))}
          </div>
        </div>
      )}
      */}

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t px-4 py-2 text-xs flex-shrink-0">
          {suggestions.map((suggestion) => (
            <Badge
              key={suggestion.id}
              variant="secondary"
              className="cursor-pointer"
              onClick={() => sendMessage(suggestion.prompt)}
            >
              {suggestion.title}
            </Badge>
          ))}
        </div>
      )}

      <div className="border-t px-4 py-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <Textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={hasReachedLimit ? "Upgrade to continue chatting..." : "Ask a question or describe what you're practicing..."}
            className="min-h-[56px] flex-1 resize-none"
            disabled={isInitializing || hasReachedLimit}
          />
          <Button
            onClick={handleSend}
            disabled={isSending || isInitializing || !inputValue.trim() || hasReachedLimit}
            className="h-[56px] w-12"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
